import {
  GAME_ACTIONS,
  type BlindKingsGame,
  type GamePiece,
} from '@blind-kings/game-core';
import {
  ACTION_COUNT,
  ACTION_DIRECTIONS,
  ATTACK_OFFSET,
  CELL_COUNT,
  FLIP_OFFSET,
  MOVE_OFFSET,
  type ActionDirection,
} from './constants';
import { actingSeat } from './perspective';

export type LegalAction =
  | {
      index: number;
      type: typeof GAME_ACTIONS.FLIP;
      pieceId: number;
      direction?: undefined;
    }
  | {
      index: number;
      type: typeof GAME_ACTIONS.MOVE;
      pieceId: number;
      direction: ActionDirection;
    }
  | {
      index: number;
      type: typeof GAME_ACTIONS.ATTACK;
      pieceId: number;
      direction: ActionDirection;
    };

export function cellIndex(y: number, x: number): number {
  return y * 8 + x;
}

export function flipIndex(cell: number): number {
  return FLIP_OFFSET + cell;
}

/** Plane-major: index = MOVE_OFFSET + direction * 32 + cell */
export function moveIndex(cell: number, dirIndex: number): number {
  return MOVE_OFFSET + dirIndex * CELL_COUNT + cell;
}

export function attackIndex(cell: number, dirIndex: number): number {
  return ATTACK_OFFSET + dirIndex * CELL_COUNT + cell;
}

export function decodeActionLayout(index: number): {
  type: (typeof GAME_ACTIONS)[keyof typeof GAME_ACTIONS];
  cell: number;
  direction?: ActionDirection;
} {
  if (index < 0 || index >= ACTION_COUNT) {
    throw new Error(`Action index out of range: ${index}`);
  }
  const plane = Math.floor(index / CELL_COUNT);
  const cell = index % CELL_COUNT;
  if (plane === 0) {
    return { type: GAME_ACTIONS.FLIP, cell };
  }
  if (plane < 5) {
    return {
      type: GAME_ACTIONS.MOVE,
      cell,
      direction: ACTION_DIRECTIONS[plane - 1],
    };
  }
  return {
    type: GAME_ACTIONS.ATTACK,
    cell,
    direction: ACTION_DIRECTIONS[plane - 5],
  };
}

function pieceAtCell(game: BlindKingsGame, cell: number): GamePiece {
  const y = Math.floor(cell / 8);
  const x = cell % 8;
  const found = (game.gameBoard.allPieces as GamePiece[]).find(
    (piece) => piece.location.y === y && piece.location.x === x
  );
  if (!found) {
    throw new Error(`No piece at cell ${cell} (${y},${x})`);
  }
  return found;
}

export function decodeActionIndex(
  game: BlindKingsGame,
  index: number
): {
  type: (typeof GAME_ACTIONS)[keyof typeof GAME_ACTIONS];
  pieceId: number;
  direction?: ActionDirection;
} {
  const layout = decodeActionLayout(index);
  const piece = pieceAtCell(game, layout.cell);
  return {
    type: layout.type,
    pieceId: piece.id,
    ...(layout.direction ? { direction: layout.direction } : {}),
  };
}

export function enumerateLegalActions(game: BlindKingsGame): LegalAction[] {
  const me = actingSeat(game);
  const out: LegalAction[] = [];

  for (const piece of game.gameBoard.allPieces as GamePiece[]) {
    const cell = cellIndex(piece.location.y, piece.location.x);
    if (piece.isUnturned) {
      out.push({
        index: flipIndex(cell),
        type: GAME_ACTIONS.FLIP,
        pieceId: piece.id,
      });
      continue;
    }
    if (!piece.isActive || piece.teamIndex !== me) continue;

    for (let d = 0; d < ACTION_DIRECTIONS.length; d += 1) {
      const direction = ACTION_DIRECTIONS[d];
      if (piece.canMoveByDirection(direction)) {
        out.push({
          index: moveIndex(cell, d),
          type: GAME_ACTIONS.MOVE,
          pieceId: piece.id,
          direction,
        });
      }
      if (piece.canAttackByDirection(direction)) {
        out.push({
          index: attackIndex(cell, d),
          type: GAME_ACTIONS.ATTACK,
          pieceId: piece.id,
          direction,
        });
      }
    }
  }

  return out;
}

/** Float32 mask: 1 = legal, 0 = illegal (length ACTION_COUNT). */
export function actionMask(game: BlindKingsGame): Float32Array {
  const mask = new Float32Array(ACTION_COUNT);
  for (const action of enumerateLegalActions(game)) {
    mask[action.index] = 1;
  }
  return mask;
}

export function assertActionLegal(game: BlindKingsGame, index: number): LegalAction {
  const legal = enumerateLegalActions(game);
  const found = legal.find((a) => a.index === index);
  if (!found) {
    throw new Error(`Illegal action index ${index} for seat ${actingSeat(game)}`);
  }
  return found;
}

export { CELL_COUNT };
